import apiClient from './api';
import type { ApiResponse } from '@hyvay/shared-types';

export interface AccountProfile {
    id: string;
    email: string;
    fullName: string | null;
    company: string | null;
    avatar: string | null;
    provider: string | null;
    credits: number;
    createdAt?: string;
}

export type CreditKind = 'CHAT' | 'PUBLISH' | 'VIDEO_RENDER' | 'TOPUP' | 'REDEEM' | 'BONUS' | 'OPENING' | 'ADJUSTMENT';

export interface CreditTransactionRow {
    id: string;
    delta: number;
    balanceAfter: number;
    kind: CreditKind;
    description: string;
    refId: string | null;
    createdAt: string;
}

export interface PaymentRow {
    id: string;
    orderId: string;
    amount: number;
    credits: number | null;
    status: string;
    createdAt: string;
}

export interface ReceiptData {
    orderId: string;
    amount: number;
    credits: number | null;
    status: string;
    createdAt: string;
    customer: { name: string | null; email: string | null; company: string | null };
}

const fail = (e: any, fallback: string) =>
    ({ success: false, message: e?.response?.data?.message || e?.message || fallback }) as any;

export const accountService = {
    async getProfile(): Promise<ApiResponse<AccountProfile>> {
        try {
            return (await apiClient.get<ApiResponse<AccountProfile>>('/account/profile')).data;
        } catch (e) {
            return fail(e, 'Gagal memuat profil');
        }
    },

    async updateProfile(body: { fullName?: string; company?: string }): Promise<ApiResponse<AccountProfile>> {
        try {
            return (await apiClient.patch<ApiResponse<AccountProfile>>('/account/profile', body)).data;
        } catch (e) {
            return fail(e, 'Gagal menyimpan profil');
        }
    },

    async uploadAvatar(file: File): Promise<ApiResponse<{ avatar: string }>> {
        try {
            const form = new FormData();
            form.append('file', file);
            // apiClient defaults to application/json; clearing it lets the browser set
            // multipart/form-data WITH the boundary, without which multer sees no file.
            return (
                await apiClient.post<ApiResponse<{ avatar: string }>>('/account/avatar', form, {
                    headers: { 'Content-Type': undefined },
                    timeout: 0,
                })
            ).data;
        } catch (e) {
            return fail(e, 'Gagal mengunggah foto');
        }
    },

    async getCredits(): Promise<ApiResponse<{ credits: number }>> {
        try {
            return (await apiClient.get<ApiResponse<{ credits: number }>>('/account/credits')).data;
        } catch (e) {
            return fail(e, 'Gagal memuat saldo');
        }
    },

    async getTransactions(
        cursor?: string,
    ): Promise<ApiResponse<{ rows: CreditTransactionRow[]; nextCursor: string | null }>> {
        try {
            const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
            return (
                await apiClient.get<ApiResponse<{ rows: CreditTransactionRow[]; nextCursor: string | null }>>(
                    `/account/transactions${q}`,
                )
            ).data;
        } catch (e) {
            return fail(e, 'Gagal memuat transaksi');
        }
    },

    async getPayments(): Promise<ApiResponse<PaymentRow[]>> {
        try {
            return (await apiClient.get<ApiResponse<PaymentRow[]>>('/account/payments')).data;
        } catch (e) {
            return fail(e, 'Gagal memuat pembayaran');
        }
    },

    async redeem(code: string): Promise<ApiResponse<{ credits: number; balance: number }>> {
        try {
            return (await apiClient.post<ApiResponse<{ credits: number; balance: number }>>('/account/redeem', { code }))
                .data;
        } catch (e) {
            return fail(e, 'Gagal redeem kode');
        }
    },

    /** Irreversible. The server re-checks that `confirmEmail` matches the account. */
    async deleteAccount(confirmEmail: string): Promise<ApiResponse<void>> {
        try {
            return (await apiClient.delete<ApiResponse<void>>('/account', { data: { confirmEmail } })).data;
        } catch (e) {
            return fail(e, 'Failed to delete account');
        }
    },

    async getReceipt(orderId: string): Promise<ApiResponse<ReceiptData>> {
        try {
            return (await apiClient.get<ApiResponse<ReceiptData>>(`/account/receipt/${orderId}`)).data;
        } catch (e) {
            return fail(e, 'Gagal memuat kwitansi');
        }
    },
};
